import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import RBAC from "../../api/RBAC";
import Input from "../form/Input";
import { toast } from "react-hot-toast";
import { useDispatch } from "react-redux";
import { setRefreshRole } from "../../store/authSlice";

export default function EditRoleModal({ open, setOpen, role, onSuccess }) {
  const dispatch = useDispatch();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (role) {
      setName(role.label || role.role_name || "");
    }
  }, [role]);

  const handleUpdateRole = async () => {
    if (!name || name.trim() === "") {
      setNameError(true);
      return;
    }

    setNameError(false);
    setLoading(true);

    try {
      const response = await RBAC.updateRoleName(role.value, name.trim());
      if (response) {
        toast.success("Role name updated successfully");
        dispatch(setRefreshRole(true));
        setOpen(false);
        setName("");
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error("Error updating role name:", error);
      if (error?.response?.status === 422) {
        toast.error("Role name already exists or is invalid");
      } else {
        toast.error("Failed to update role name. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName("");
    setNameError(false);
    setOpen(false);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-100" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-70 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg p-8 text-center shadow-xl transition-all sm:my-8 sm:p-6 w-full max-w-md bg-white">
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-primary-500 text-2xl">
                      Edit Role Name
                    </h4>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={handleClose}
                    >
                      <i className="fas fa-times text-xl"></i>
                    </button>
                  </div>
                  
                  <div className="text-left">
                    <Input
                      className="my-3"
                      name="name"
                      label="Role Name"
                      type="text"
                      value={name}
                      error={nameError ? "Role Name is required" : null}
                      placeholder="Enter role name"
                      onChange={(e) => {
                        setName(e.target.value);
                        setNameError(false);
                      }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleUpdateRole();
                        }
                      }}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
                    <button
                      type="button"
                      className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      onClick={handleClose}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed inline-flex items-center justify-center"
                      onClick={handleUpdateRole}
                      disabled={loading || !name || name.trim() === ""}
                    >
                      {loading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save mr-2"></i>
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
